import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';
import { Progress } from '../ui/progress';
import {
  getSyncStatus,
  backfillUntilComplete,
  syncInsights,
  resetInsights,
  type SyncStatus,
  type BackfillResult,
} from '../../api/ingestion';
import { toast } from 'sonner';
import { Loader2, AlertTriangle, CheckCircle2, RefreshCw, Trash2 } from 'lucide-react';

interface SyncSettingsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  projectId: string;
  buildId: number;
}

export function SyncSettingsModal({
  open,
  onOpenChange,
  projectId,
  buildId,
}: SyncSettingsModalProps) {
  const [status, setStatus] = useState<SyncStatus | null>(null);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [backfilling, setBackfilling] = useState(false);
  const [backfillProgress, setBackfillProgress] = useState<BackfillResult | null>(null);

  // Backfill form state
  const [startDate, setStartDate] = useState('2024-01-01');
  const [endDate, setEndDate] = useState(new Date().toISOString().split('T')[0]);
  const [includeEmails, setIncludeEmails] = useState(true);
  const [includeChatter, setIncludeChatter] = useState(true);

  // Reset confirmation
  const [resetConfirm, setResetConfirm] = useState('');

  useEffect(() => {
    if (open) {
      loadStatus();
    }
  }, [open, projectId, buildId]);

  const loadStatus = async () => {
    setLoading(true);
    try {
      const data = await getSyncStatus(projectId, buildId);
      setStatus(data);
    } catch (error) {
      console.error('Failed to load sync status:', error);
      toast.error('Failed to load sync status');
    } finally {
      setLoading(false);
    }
  };

  const handleBackfill = async () => {
    const sources: Array<'email' | 'chatter'> = [];
    if (includeEmails) sources.push('email');
    if (includeChatter) sources.push('chatter');

    if (sources.length === 0) {
      toast.error('Select at least one source to backfill');
      return;
    }

    setBackfilling(true);
    setBackfillProgress(null);

    try {
      const result = await backfillUntilComplete(
        projectId,
        {
          buildId,
          startDate,
          endDate,
          sources,
          batchSize: 100,
        },
        (progress) => {
          setBackfillProgress(progress);
        }
      );

      toast.success(
        `Backfill complete! Synced ${result.synced.emails || 0} emails and ${
          result.synced.chatter || 0
        } chatter messages`
      );

      await loadStatus();
    } catch (error) {
      console.error('Backfill failed:', error);
      toast.error(`Backfill failed: ${error}`);
    } finally {
      setBackfilling(false);
      setBackfillProgress(null);
    }
  };

  const handleSyncNow = async () => {
    setSyncing(true);
    try {
      const result = await syncInsights(projectId, {
        buildId,
        sources: ['email', 'chatter'],
      });

      toast.success(
        `Synced ${result.synced.emails || 0} new emails and ${
          result.synced.chatter || 0
        } new chatter messages`
      );

      await loadStatus();
    } catch (error) {
      console.error('Sync failed:', error);
      toast.error(`Sync failed: ${error}`);
    } finally {
      setSyncing(false);
    }
  };

  const handleReset = async () => {
    if (resetConfirm !== 'DELETE') {
      toast.error('Type DELETE to confirm');
      return;
    }

    setResetting(true);
    try {
      const result = await resetInsights(projectId, {
        buildId,
        sources: ['email', 'chatter'],
        confirm: true,
      });

      toast.success(
        `Reset complete! Deleted ${result.deleted.emails || 0} emails and ${
          result.deleted.chatter || 0
        } chatter messages`
      );

      setResetConfirm('');
      await loadStatus();
    } catch (error) {
      console.error('Reset failed:', error);
      toast.error(`Reset failed: ${error}`);
    } finally {
      setResetting(false);
    }
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return 'Never';
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} min${diffMins > 1 ? 's' : ''} ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Data Sync Settings</DialogTitle>
          <DialogDescription>
            Manage email and chatter data ingestion from Odoo
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="space-y-6">
            {/* Current Status */}
            <div className="space-y-3">
              <h3 className="text-sm font-semibold">Current Status</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="border rounded-lg p-4 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">Emails</span>
                    <CheckCircle2 className="h-4 w-4 text-green-500" />
                  </div>
                  <p className="text-2xl font-bold">
                    {status?.email?.totalMessages?.toLocaleString() || 0}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Last sync: {formatDate(status?.email?.lastSyncAt || null)}
                  </p>
                </div>

                <div className="border rounded-lg p-4 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">Chatter</span>
                    <CheckCircle2 className="h-4 w-4 text-green-500" />
                  </div>
                  <p className="text-2xl font-bold">
                    {status?.chatter?.totalMessages?.toLocaleString() || 0}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Last sync: {formatDate(status?.chatter?.lastSyncAt || null)}
                  </p>
                </div>
              </div>

              {status?.activeJobs && status.activeJobs.length > 0 && (
                <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin text-blue-600" />
                    <span className="text-sm font-medium text-blue-900">
                      Active job: {status.activeJobs[0].type} ({status.activeJobs[0].progress}%)
                    </span>
                  </div>
                </div>
              )}
            </div>

            {/* Backfill Section */}
            <div className="space-y-3 border-t pt-6">
              <h3 className="text-sm font-semibold">Backfill Historical Data</h3>
              <p className="text-sm text-muted-foreground">
                Import emails and chatter from a specific date range
              </p>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="startDate">From Date</Label>
                  <Input
                    id="startDate"
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    disabled={backfilling}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="endDate">To Date</Label>
                  <Input
                    id="endDate"
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    disabled={backfilling}
                  />
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="includeEmails"
                    checked={includeEmails}
                    onCheckedChange={(checked) => setIncludeEmails(!!checked)}
                    disabled={backfilling}
                  />
                  <label
                    htmlFor="includeEmails"
                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                  >
                    Emails
                  </label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="includeChatter"
                    checked={includeChatter}
                    onCheckedChange={(checked) => setIncludeChatter(!!checked)}
                    disabled={backfilling}
                  />
                  <label
                    htmlFor="includeChatter"
                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                  >
                    Chatter
                  </label>
                </div>
              </div>

              {backfillProgress && !backfillProgress.complete && (
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>Syncing...</span>
                    <span>
                      {backfillProgress.synced.emails || 0} emails,{' '}
                      {backfillProgress.synced.chatter || 0} chatter
                    </span>
                  </div>
                  <Progress value={50} className="h-2" />
                </div>
              )}

              <Button onClick={handleBackfill} disabled={backfilling} className="w-full">
                {backfilling ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Backfilling...
                  </>
                ) : (
                  'Start Backfill'
                )}
              </Button>
            </div>

            {/* Incremental Sync */}
            <div className="space-y-3 border-t pt-6">
              <h3 className="text-sm font-semibold">Continuous Sync</h3>
              <p className="text-sm text-muted-foreground">
                Sync only new messages since last sync
              </p>

              <Button onClick={handleSyncNow} disabled={syncing} className="w-full" variant="outline">
                {syncing ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Syncing...
                  </>
                ) : (
                  <>
                    <RefreshCw className="mr-2 h-4 w-4" />
                    Sync Now
                  </>
                )}
              </Button>
            </div>

            {/* Danger Zone */}
            <div className="space-y-3 border-t border-red-200 pt-6">
              <div className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-red-600" />
                <h3 className="text-sm font-semibold text-red-600">Danger Zone</h3>
              </div>
              <p className="text-sm text-muted-foreground">
                This will permanently delete all emails, chatter, and analyses for this project.
              </p>

              <div className="space-y-2">
                <Label htmlFor="resetConfirm">Type DELETE to confirm</Label>
                <Input
                  id="resetConfirm"
                  value={resetConfirm}
                  onChange={(e) => setResetConfirm(e.target.value)}
                  placeholder="DELETE"
                  disabled={resetting}
                />
              </div>

              <Button
                onClick={handleReset}
                disabled={resetting || resetConfirm !== 'DELETE'}
                variant="destructive"
                className="w-full"
              >
                {resetting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Resetting...
                  </>
                ) : (
                  <>
                    <Trash2 className="mr-2 h-4 w-4" />
                    Reset All Data
                  </>
                )}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
